/**
 * RequireSignup.tsx
 * 
 * A wrapper component that redirects unauthenticated users to the signup flow.
 * Used for pages that require user authentication via signup (not login).
 * 
 * Usage:
 * <RequireSignup redirectPath="/your/protected/path">
 *   <YourProtectedComponent />
 * </RequireSignup>
 */

import React, { useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useSignup } from '../../useSignup';
import { AlyzeLoadingSpinner } from '../../../componentsV1/shared';
import { makeStyles } from '@fluentui/react-components';

const useStyles = makeStyles({
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        gap: '16px',
        padding: '24px',
    }
});

interface RequireSignupProps {
    /** The component(s) to render when authenticated */
    children: React.ReactNode;
    /** The path to redirect back to after signup completes */
    redirectPath: string;
}

export const RequireSignup: React.FC<RequireSignupProps> = ({ children, redirectPath }) => {
    const auth = useAuth();
    const { signupRedirect } = useSignup();
    const styles = useStyles();
    const [isRedirecting, setIsRedirecting] = useState(false);

    useEffect(() => {
        // If not authenticated and not already loading/navigating, trigger signup
        if (!auth.isLoading && !auth.isAuthenticated && !auth.activeNavigator && !isRedirecting) {
            setIsRedirecting(true);
            signupRedirect(redirectPath).catch((error) => {
                console.error("Signup redirect failed:", error);
                setIsRedirecting(false);
            });
        }
    }, [auth.isLoading, auth.isAuthenticated, auth.activeNavigator, signupRedirect, redirectPath, isRedirecting]);

    // Show loading state while auth is initializing
    if (auth.isLoading) {
        return (
            <div className={styles.loadingContainer}>
                <AlyzeLoadingSpinner message="Loading..." />
            </div>
        );
    }

    // Show redirecting state
    if (isRedirecting || auth.activeNavigator) {
        return (
            <div className={styles.loadingContainer}>
                <AlyzeLoadingSpinner message="Redirecting to sign up..." />
            </div>
        );
    }

    // Show error state if there's an auth error
    if (auth.error) {
        return (
            <div className={styles.loadingContainer}>
                <AlyzeLoadingSpinner message={`Authentication error: ${auth.error.message}`} />
                <div>Please try again.</div>
            </div>
        );
    }

    // Only render children if authenticated
    if (auth.isAuthenticated) {
        return <>{children}</>;
    }

    // Default loading state (shouldn't normally reach here)
    return (
        <div className={styles.loadingContainer}>
            <AlyzeLoadingSpinner message="Authenticating..." />
        </div>
    );
};
